import './styles/WatchManager.scss';

import { ArrowClockwise, PauseCircle, XCircle } from '@phosphor-icons/react';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnmount } from 'react-use';

import { cn } from '../../../../helpers';
import { parseError } from '../../../../helpers/errorUtils';
import { useClassHelper, useGoogleFonts } from '../../../../hooks';
import { TextEditorContent, WebsheetTemplateProps } from '../../../../types';
import AppleLoading from '../../../AppleLoading';
import { Dropdown } from '../../../Dropdown/Dropdown';
import { IconRenderer } from '../../../IconRenderer';
import { Stack } from '../../../Stack';
import { TextEditor } from '../../../TextEditor';
import { UnexpectedErrorWebsheet } from '../../../UnexpectedErrorWebsheet/UnexpectedErrorWebsheet';
import { Button } from '../../components/Button';
import { DefaultErrorTemplate } from '../../components/DefaultErrorTemplate';
import { ImageHeader } from '../../components/ImageHeader';
import { PlanInfo } from '../../components/PlanInfo';
import { DATADOG_ACTIONS } from '../../constants';
import { isTextChildrenEmpty, renderParagraphOrRichText } from '../../helpers';
import { trackSdkCall } from '../../helpers/trackSdkCall';
import { useDataDogActions } from '../../hooks/useDataDogActions';
import { useGetStyles } from '../../hooks/useGetStyles';
import { useVisibilityCheck } from '../../hooks/useVisibilityCheck';
import {
  componentName as watchComponentName,
  defaultManageSelectedField,
  manageErrorFields,
  manageFamilyPlanFields,
  managePlanActions,
} from './constants';
import {
  buildManageErrorLocalisation,
  resolveManageErrorText,
  resolveManageText,
} from './helpers';
import {
  useFetchWatchManage,
  useManagerPreviewSync,
  useWatchImage,
} from './hooks';
import { useWatchManagerStore } from './store/manager';
import { TermsConditionsW } from './TermsConditions_W';
import { Device, SubscriptionType } from './types';

const componentName = 'Watch_M';

const ActionIcon = {
  suspend: <PauseCircle size={30} />,
  reactivate: <ArrowClockwise size={30} />,
  cancel: <XCircle size={30} />,
};

const ACTION_PROGRESS = {
  suspend: 'suspending',
  reactivate: 'reactivating',
  cancel: 'cancelling',
} as const;

const ACTION_RESULT = {
  suspend: 'suspended',
  reactivate: 'reactivated',
  cancel: 'cancelled',
} as const;

const ACTION_TYPE = {
  suspend: 'suspend',
  reactivate: 'reactivate',
  cancel: 'cancel',
} as const;

type ActionType = (typeof ACTION_TYPE)[keyof typeof ACTION_TYPE];

const DATA_DOG_CONTEXT = 'watch_manage';

type ManagerView =
  | { type: 'terms' }
  | { type: 'error' }
  | { type: 'confirmCancel' }
  | { type: 'feedback'; loading: boolean }
  | { type: 'actions' };

// Derives the current view from the store flags. Pure projection — sets
// nothing. The precedence replicates EXACTLY the order of the early-returns,
// so flag combinations resolve deterministically instead of relying on the
// implicit ordering scattered across the render.
const getManagerView = (
  m: {
    termsAndConditions: boolean;
    isError: boolean;
    confirmCancel: boolean;
    actionSucceeded: boolean;
    actionLoading: boolean;
    viewActions: boolean;
  },
  preview: { isErrorFieldsSelected: boolean },
): ManagerView => {
  if (m.termsAndConditions) {
    return { type: 'terms' };
  }

  if (m.isError || preview.isErrorFieldsSelected) {
    return { type: 'error' };
  }

  if (m.confirmCancel) {
    return { type: 'confirmCancel' };
  }

  if (m.actionSucceeded || m.actionLoading) {
    return { type: 'feedback', loading: m.actionLoading };
  }

  return { type: 'actions' };
};

function WatchManager(props: WebsheetTemplateProps) {
  const {
    services,
    deviceSdk,
    state,
    values,
    text,
    isPortalPreview,
    selectedField: selectedFieldProp,
    portalMessenger,
    deviceType,
  } = props;

  // Preview with nothing focused behaves as if the manage landing title were
  // selected; runtime (non-preview) keeps the raw prop untouched.
  const selectedField =
    isPortalPreview && !selectedFieldProp
      ? defaultManageSelectedField
      : selectedFieldProp;

  const { t } = useTranslation();

  const { actions, dataDogLogger, isPortalTest } = useDataDogActions({
    isPortalPreview,
    actionKey: DATA_DOG_CONTEXT,
  });

  const isPreviewLayout = isPortalPreview || isPortalTest;

  const c = useClassHelper(componentName);
  const watchClasses = useClassHelper(watchComponentName);

  const {
    managerValues,
    setManagerValues,
    setManagerTermsAndConditions,
    setManagerActiveWatch,
  } = useWatchManagerStore();

  // Ref on the main content render. checkPresence is off so the other view
  // branches (terms/error/confirmCancel/feedback) — where this ref is null —
  // stay no-ops instead of firing a false MISSING_DOM.
  const containerRef = useRef<HTMLElement>(null);

  useVisibilityCheck(containerRef, {
    enabled: !isPortalPreview,
    checkPresence: false,
    logger: dataDogLogger,
    errorAction: DATADOG_ACTIONS.errors.template_error,
    flow: 'render',
  });

  const handleImageError = (imageUrl: string) => {
    if (isPortalPreview) {
      return;
    }

    dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
      flow: 'render_logo',
      statusCode: 0,
      errorCode: 'IMAGE_LOAD_FAILED',
      errorMessage: `Logo image failed to load: ${imageUrl}`,
      imageUrl,
    });
  };

  useGoogleFonts([values.fontParagraph, values.fontTitle]);

  const {
    data: watchData,
    isPending: watchDataLoading,
    error: watchDataError,
  } = useFetchWatchManage({ services, state });

  const {
    activeWatch,
    viewActions,
    errorCode,
    buttonDisabled,
    confirmCancel,
    actionSucceeded,
    actionLoading,
    currentAction,
    subscriptionType,
  } = managerValues;

  // "linked" → sa-watch banner and sa-watch localised text
  // "paired" → Multi-SIM banner and Multi-SIM localised text
  // Preview flip: selecting any manage family_plan_ twin previews the linked
  // variant even though there is no runtime subscriptionType yet.
  const isLinked =
    subscriptionType === 'linked' ||
    (isPortalPreview &&
      manageFamilyPlanFields.includes(selectedField?.field as string));

  // `isLinked` already covers the preview flip (family_plan_ field selected),
  // so the banner follows the toggled side. A blanket `|| isPortalPreview`
  // here used to pin the preview banner to the family image permanently.
  const { headerImageUrl, imageConfig } = useWatchImage(
    values,
    deviceType,
    isLinked,
  );

  const getStyles = useGetStyles({ values });

  const actionFeedbackTitle = useMemo(() => {
    if (!text) {
      return '';
    }

    if (actionLoading) {
      return (
        resolveManageText(`action_running_${currentAction}`, isLinked, text) ||
        `${ACTION_PROGRESS[currentAction]} your plan`
      );
    }

    return (
      resolveManageText(`action_succeeded_${currentAction}`, isLinked, text) ||
      `Your plan has been ${ACTION_RESULT[currentAction]}`
    );
  }, [currentAction, text, actionSucceeded, isLinked]);

  const actionFeedbackDescription = useMemo(() => {
    if (!text) {
      return '';
    }

    if (actionLoading) {
      return (
        resolveManageText(
          `action_running_${currentAction}_description`,
          isLinked,
          text,
        ) || `We are ${ACTION_PROGRESS[currentAction]} your plan...`
      );
    }

    return (
      resolveManageText(
        `action_succeeded_${currentAction}_description`,
        isLinked,
        text,
      ) || `Your plan has been ${ACTION_RESULT[currentAction]} successfully`
    );
  }, [currentAction, text, actionSucceeded, isLinked]);

  const contactUsText = resolveManageText<TextEditorContent[]>(
    'contact_us',
    isLinked,
    text,
  );

  const isContactUsTextEmpty = useMemo(() => {
    return isTextChildrenEmpty(contactUsText);
  }, [contactUsText]);

  const isErrorFieldsSelected = useMemo(() => {
    if (selectedField && isPortalPreview) {
      return manageErrorFields.includes(selectedField.field);
    }

    return false;
  }, [selectedField?.field]);

  const isActionSelected = useMemo(() => {
    if (viewActions && isPortalPreview) {
      return managePlanActions.includes(selectedField?.field);
    }

    return false;
  }, [selectedField?.field, isPortalPreview, viewActions]);

  const selectActiveWatch = (planId: string) => {
    // This is trying to get the device from a map, instead of filtering the devices array
    const device = watchData.devicesMap.get(planId) as Device;

    const deviceDetails = watchData.plansMap.get(device.plan_offer_id);

    setManagerActiveWatch({
      iccid: device.iccid,
      actions: device.actions,
      details: deviceDetails?.details,
      hasActions: device?.actions?.length > 0,
    });
  };

  useEffect(() => {
    if (watchDataError) {
      const {
        errorCode,
        errorMessage,
        statusCode,
        subscriptionType: errorSubscriptionType,
      } = parseError(watchDataError);

      setManagerValues({
        isError: true,
        errorCode: errorCode ?? 'UNKNOWN',
        // Body value wins; the store's existing value (GET-body fallback,
        // set below) covers codes that omit it (e.g. INVALID_STATE).
        ...(errorSubscriptionType && {
          subscriptionType: errorSubscriptionType as SubscriptionType,
        }),
      });

      // Skip DataDog tracking in preview/test mode to avoid polluting production metrics
      if (!isPortalPreview) {
        dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
          flow: 'load_manage',
          statusCode,
          errorCode: errorCode ?? 'UNKNOWN',
          errorMessage,
        });
      }
    }
  }, [watchDataError]);

  useEffect(() => {
    if (watchData && !isPortalPreview) {
      if (!watchData?.hasDevices) {
        dataDogLogger.warning(DATADOG_ACTIONS.errors.template_error, {
          flow: 'manage',
          errorCode: 'NO_DEVICES_FOUND',
          errorMessage: 'Devices key empty',
        });
      }

      if (!watchData?.hasPlans) {
        dataDogLogger.warning(DATADOG_ACTIONS.errors.template_error, {
          flow: 'manage',
          errorCode: 'NO_PLAN_FOUND',
          errorMessage: 'Plan key empty',
        });
      }
    }
  }, [watchData]);

  // Tracks when the user actually sees the success screen, separate from
  // action_success which fires in handleAction when the API returns. A render
  // bug could set actionSucceeded without the feedback screen ever mounting —
  // this catches that gap.
  useEffect(() => {
    if (actionSucceeded && !isPortalPreview) {
      dataDogLogger.log(actions?.success_screen_shown, {
        flow: 'perform_action',
        action: currentAction,
      });
    }
  }, [actionSucceeded]);

  // Persist the subscription type from GET /watch/manage into the store so the
  // manage view can branch linked vs paired for the rest of the session.
  // Missing attribute falls back to the 'paired' store default.
  useEffect(() => {
    if (watchData?.subscription_type) {
      setManagerValues({ subscriptionType: watchData.subscription_type });
    }
  }, [watchData?.subscription_type]);

  // Tag the RUM session with the subscription mode so manage events (suspend,
  // reactivate, cancel) are segmentable by paired vs linked — e.g. cancelling a
  // plan to switch modes (Option C). Runtime only.
  useEffect(() => {
    if (isPreviewLayout) {
      return;
    }
    dataDogLogger.addGlobal('subscription_type', subscriptionType);
  }, [subscriptionType, isPreviewLayout]);

  // The device-selection screen has been removed, so auto-select the first
  // available device to drive straight into the actions view.
  useEffect(() => {
    if (
      watchData?.hasDevices &&
      watchData.devices.length > 0 &&
      !activeWatch &&
      !isPortalPreview
    ) {
      const [device] = watchData.devices;
      selectActiveWatch(device.iccid);
    }
  }, [watchData, activeWatch]);

  // Refresh viewActions, this is saved in a store, when you click in another usecase (transfer or activation)
  // It will look weird, so we need to restore everything, but only on portalpreview
  useUnmount(() => {
    if (isPortalPreview) {
      setManagerValues({ viewActions: false });
    }
  });

  // This is the hook that keeps preview updated when you change between fields on the actions form.
  useManagerPreviewSync({
    selectedField,
    isPortalPreview,
  });

  const handleAction = async (
    ev: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    const action = ev.currentTarget.dataset.action as ActionType;

    if (isPortalPreview) {
      return;
    }

    // The action button click is already tracked via its data-dd-action-name
    // (RUM auto-tracking) — no explicit log here to avoid double emission.

    try {
      deviceSdk.hideCancelButtonSelected();

      setManagerValues({
        buttonDisabled: true,
        currentAction: action,
        actionLoading: true,
      });

      if (action === ACTION_TYPE.cancel && !confirmCancel) {
        setManagerValues({
          confirmCancel: true,
          actionLoading: true,
        });
        return;
      }

      if (action === ACTION_TYPE.cancel && confirmCancel) {
        setManagerValues({
          buttonDisabled: false,
          confirmCancel: false,
          actionLoading: true,
        });
      }

      // Mark the action start so DataDog can derive submit→success latency
      // from the view-relative custom timings.
      dataDogLogger.addTiming(DATADOG_ACTIONS.timings.action_submitted);

      const data = await services.performAction({
        iccid: activeWatch.iccid,
        action,
        state,
      });

      if (portalMessenger) {
        portalMessenger.propagateSuccess();
      }

      setManagerValues({
        buttonDisabled: false,
        actionLoading: false,
        actionSucceeded: true,
      });

      // --- Success signals ---
      // succeeded − submitted = action latency. Session stays LIVE; stopSession
      // is deferred to the feedback-screen close.
      dataDogLogger.log(actions?.action_success, {
        flow: 'perform_action',
        action,
      });
      dataDogLogger.addTiming(DATADOG_ACTIONS.timings.action_succeeded);
      dataDogLogger.addGlobal('completed', true);
      dataDogLogger.log(DATADOG_ACTIONS.success.template_success, {
        flow: 'perform_action',
        action,
      });

      switch (action) {
        case ACTION_TYPE.suspend: {
          deviceSdk.watchCancellation();
          break;
        }
        case ACTION_TYPE.reactivate: {
          deviceSdk.watchActivation(data);
          break;
        }
        case ACTION_TYPE.cancel: {
          deviceSdk.watchCancellation();
          setManagerValues({ confirmCancel: false });
          break;
        }
        default: {
          break;
        }
      }
    } catch (error) {
      const {
        errorCode,
        errorMessage,
        statusCode,
        subscriptionType: errorSubscriptionType,
      } = parseError(error);

      if (portalMessenger) {
        portalMessenger.propagateError();
      }

      setManagerValues({
        isError: true,
        errorCode: errorCode ?? 'UNKNOWN',
        // Body value wins; the store's existing value (GET-body fallback)
        // covers codes that omit it (e.g. INVALID_STATE, SESSION_NOT_FOUND).
        ...(errorSubscriptionType && {
          subscriptionType: errorSubscriptionType as SubscriptionType,
        }),
      });

      // Template-level error tracking; `action` ctx disambiguates which
      // operation (suspend/reactivate/cancel) failed.
      dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
        flow: 'perform_action',
        action,
        statusCode,
        errorCode: errorCode ?? 'UNKNOWN',
        errorMessage,
      });

      deviceSdk.showCancelButtonSelected();
    }
  };

  const view = getManagerView(managerValues, { isErrorFieldsSelected });

  if (view.type === 'terms') {
    return (
      <TermsConditionsW
        {...props}
        onClose={() => {
          if (isPortalPreview) {
            return;
          }

          setManagerTermsAndConditions(false);
        }}
        data-dd-action-name={actions?.terms_and_conditions_closed}
      />
    );
  }

  if (view.type === 'error') {
    // Preview: selecting a manage error field (or its family_plan_ twin)
    // previews that field's copy, overriding the store errorCode. Runtime
    // (and preview with no matching selection): the store errorCode drives
    // it, unchanged — including the INVALID_STATE branch below.
    const previewErrorCode =
      isPortalPreview &&
      selectedField?.field &&
      manageErrorFields.includes(selectedField.field)
        ? selectedField.field
        : errorCode;

    if (errorCode === 'INVALID_STATE') {
      return (
        <UnexpectedErrorWebsheet
          title={t('error.title')}
          body={t('error.body')}
          close={t('error.close')}
          onClick={() => {
            if (deviceSdk && deviceSdk.exit) {
              // exit() emits finishFlow + dismissFlow actions; stop the session
              // after so those logs are captured in it.
              trackSdkCall('exit', () => deviceSdk.exit(), dataDogLogger);
              dataDogLogger.stopSession();
              return;
            }

            // SDK method missing — the user's close does nothing. Report the
            // silent failure, then close the session.
            if (!isPortalPreview) {
              dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
                flow: 'sdk_unavailable',
                errorCode: 'SDK_METHOD_MISSING',
                errorMessage: 'deviceSdk.exit is unavailable',
                method: 'exit',
              });
              dataDogLogger.stopSession();
            }
          }}
        />
      );
    }

    return (
      <DefaultErrorTemplate
        title={resolveManageErrorText('error_title', isLinked, text) || ''}
        closeButtonText={
          resolveManageErrorText('close_button', isLinked, text) || 'Close'
        }
        errorCode={previewErrorCode}
        localisation={buildManageErrorLocalisation(text)}
        isLinked={isLinked}
        onClick={() => {
          if (isPortalPreview) {
            return;
          }

          if (portalMessenger) {
            portalMessenger.propagateError();
          }

          // decline() emits its own dismissFlow action; stop the session
          // after so that log is captured in it.
          trackSdkCall('decline', () => deviceSdk.decline(), dataDogLogger);
          dataDogLogger.stopSession();
        }}
        baseComponentName="c-Watch"
        style={getStyles as Record<string, string | number>}
        data-dd-action-name={actions?.close_error_clicked}
      />
    );
  }

  if (view.type === 'confirmCancel') {
    const cancelConfirmationText = renderParagraphOrRichText(
      resolveManageText<string | TextEditorContent[]>(
        'cancel_confirmation_text',
        isLinked,
        text,
      ),
      values?.fontParagraph,
    );

    return (
      <main className={`${c('base')} ${c('--confirmation')}`} style={getStyles}>
        <Stack
          tag="section"
          gap={4}
          direction="column"
          justify="around"
          height="full"
          className={cn('flex-1 shrink basis-0 pt-5 pb-6 px-4 w-full', {
            'pb-[36px]': isPortalPreview,
          })}
        >
          <Stack gap={4} direction="column" className={c('--bottom-gap')}>
            <h1 className={watchClasses('__title')}>
              {resolveManageText('cancel_confirmation_title', isLinked, text) ||
                'Cancel your plan'}
            </h1>
            {cancelConfirmationText ?? (
              <p className={c('--centered')}>
                <Dropdown.GoogleFont.Viewer fontFamily={values?.fontParagraph}>
                  Are you sure you want to cancel?
                </Dropdown.GoogleFont.Viewer>
              </p>
            )}
            <Button
              type="button"
              className={c('__btn-simple')}
              data-action="cancel"
              onClick={handleAction}
              data-dd-action-name={actions?.cancel_confirmation_yes_clicked}
            >
              {resolveManageText('yes_button', isLinked, text) || 'Yes'}
            </Button>
            <Button
              type="button"
              className="primary"
              onClick={() => {
                if (isPortalPreview) {
                  return;
                }

                setManagerValues({
                  confirmCancel: false,
                  actionLoading: false,
                  buttonDisabled: false,
                });
              }}
              data-dd-action-name={actions?.cancel_confirmation_no_clicked}
            >
              {resolveManageText('no_button', isLinked, text) || 'No'}
            </Button>
          </Stack>
        </Stack>
      </main>
    );
  }

  if (view.type === 'feedback') {
    return (
      <main className="c-Watch" style={getStyles}>
        <section
          className={`${watchClasses(
            '__status',
          )} flex-1 shrink basis-0 pt-5 pb-6 px-4`}
        >
          <h1 className={watchClasses('__title')}>{actionFeedbackTitle}</h1>
          <figure className={watchClasses('__status__icon')}>
            <IconRenderer variant={actionSucceeded ? 'success' : 'spinner'} />
          </figure>
          <p className={watchClasses('__paragraph')}>
            {actionFeedbackDescription || ''}
          </p>
          <Button
            type="button"
            disabled={actionLoading}
            onClick={() => {
              if (isPortalPreview) {
                return;
              }

              setManagerValues({ actionSucceeded: false });

              if (portalMessenger) {
                portalMessenger.propagateError();
              }

              // close() emits its own finishFlow action; stop the session
              // after so that log lands in it.
              trackSdkCall('close', () => deviceSdk.close(), dataDogLogger);
              dataDogLogger.stopSession();
            }}
            data-dd-action-name={actions?.close_action_feedback_clicked}
          >
            {resolveManageText('action_close', isLinked, text) || 'Close'}
          </Button>
        </section>
      </main>
    );
  }

  const renderContent = () => {
    if (watchDataLoading) {
      return (
        <>
          <AppleLoading />
        </>
      );
    }

    const renderActions = () => {
      if (activeWatch?.hasActions || isActionSelected) {
        return (
          <Fragment>
            <p className="mb-[16px]! text-base">
              <Dropdown.GoogleFont.Viewer fontFamily={values?.fontParagraph}>
                {resolveManageText('watch_actions', isLinked, text) ||
                  'What would you like to do?'}
              </Dropdown.GoogleFont.Viewer>
            </p>
            <Stack direction="column" gap={4}>
              {activeWatch?.actions?.map((action: string) => {
                const resolvedActionText = resolveManageText(
                  `action_${action}`,
                  isLinked,
                  text,
                );
                const actionText = resolvedActionText ?? `${action} Plan`;

                const actionDataDogMap = {
                  suspend: actions?.action_suspend_clicked,
                  reactivate: actions?.action_reactivate_clicked,
                  cancel: actions?.action_cancel_clicked,
                };

                return (
                  <button
                    type="button"
                    key={action}
                    data-action={action}
                    onClick={handleAction}
                    disabled={buttonDisabled}
                    aria-disabled={buttonDisabled}
                    data-dd-action-name={actionDataDogMap[action]}
                  >
                    <Stack
                      direction="column"
                      gap={3}
                      justify="center"
                      align="center"
                    >
                      {ActionIcon[action]}
                      <p
                        className={c('__watch-actions-text', {
                          '__watch-actions-text--capitalize':
                            !resolvedActionText,
                        })}
                      >
                        <Dropdown.GoogleFont.Viewer
                          fontFamily={values?.fontParagraph}
                        >
                          {actionText}
                        </Dropdown.GoogleFont.Viewer>
                      </p>
                    </Stack>
                  </button>
                );
              })}
            </Stack>
          </Fragment>
        );
      }

      if (contactUsText && !isContactUsTextEmpty) {
        return (
          <div className="contact-us">
            <TextEditor
              viewOnly
              value={contactUsText}
              valueFontParagraph={values?.fontParagraph}
            />
          </div>
        );
      }

      return null;
    };

    return (
      <Stack direction="column" gap={6}>
        <div className="content">
          {watchData?.hasDevices && (
            <PlanInfo
              name={activeWatch?.details?.[0]?.name}
              description={activeWatch?.details?.[0]?.description}
              descriptionExtended={
                activeWatch?.details?.[0]?.description_extended
              }
            />
          )}
          <div className={c('__watch-actions')}>{renderActions()}</div>
        </div>

        <div className={c('__general-actions')}>
          <Button
            className={c('__link')}
            onClick={() => {
              if (isPortalPreview) {
                return;
              }

              setManagerTermsAndConditions(true);
            }}
            data-dd-action-name={actions?.terms_and_conditions_opened}
          >
            <Dropdown.GoogleFont.Viewer fontFamily={values?.fontParagraph}>
              {resolveManageText('terms_and_conditions', isLinked, text) ||
                'Terms and Conditions'}
            </Dropdown.GoogleFont.Viewer>
          </Button>

          <Button
            type="button"
            disabled={actionLoading}
            onClick={() => {
              if (isPortalPreview) {
                return;
              }

              setManagerValues({ actionSucceeded: false });

              if (portalMessenger) {
                portalMessenger.propagateError();
              }

              // close() emits its own finishFlow action; stop the session
              // after so that log lands in it.
              trackSdkCall('close', () => deviceSdk.close(), dataDogLogger);
              dataDogLogger.stopSession();
            }}
            data-dd-action-name={actions?.close_actions_view_clicked}
          >
            {resolveManageText('action_close', isLinked, text) || 'Close'}
          </Button>
        </div>
      </Stack>
    );
  };

  // It will be only shown if there is a header image URL and view actions are enabled
  const showHeaderImage = Boolean(headerImageUrl && viewActions);

  return (
    <main
      ref={containerRef}
      className={c('base', { '--linked': isLinked })}
      style={getStyles}
    >
      <Stack
        direction="column"
        className={c('__inner', { '__inner-preview': isPreviewLayout })}
      >
        {showHeaderImage && (
          <ImageHeader
            url={headerImageUrl as string}
            alt="watch"
            isPortalPreview={true}
            imageConfig={imageConfig}
            onError={handleImageError}
          />
        )}

        <Stack
          direction="column"
          gap={4}
          tag="section"
          className="flex-1 shrink basis-0 pb-6 px-4"
        >
          <h1
            className={`${watchClasses('__title', {
              '__title--no-image': !(headerImageUrl && viewActions),
            })} pt-5`}
          >
            {resolveManageText('manage_my_plan_title', isLinked, text) ||
              'Manage My Plan'}
          </h1>
          {renderContent()}
        </Stack>
      </Stack>
    </main>
  );
}

export default WatchManager;
